import { BoardFrame, BoardLanes, BoardSkeleton } from "@board/BoardLanes";
import { ClinicRosterTables } from "@board/ClinicRoster";
import { VisitCard } from "@board/VisitCard";
import type { ClinicMapLive } from "@clinicMap/clinicMapClient";
import { LiveClinicMap } from "@clinicMap/LiveClinicMap";
import type { ClinicRosterToday } from "@perception/perceptionClient";
import { EmptyNote } from "@scribenote/brian-design-system";
import { DisplayFrame } from "@shell/DisplayFrame";
import { useEffect, useState } from "react";

import type { DisplayBoardVisit } from "./clinicBoardView";
import {
  BOARD_VIEW_LABEL,
  BOARD_VIEWS,
  type BoardRotation,
  type BoardView,
} from "./displaysClient";
import { useDisplayLiveMap } from "./displaysQueries";

/**
 * Views with time on this display and something to show. When none has
 * anything, the views with time, so a view set to 0 never comes back.
 */
const rotating = (
  rotation: BoardRotation,
  map: ClinicMapLive | undefined,
  roster: ClinicRosterToday,
): (typeof BOARD_VIEWS)[number][] => {
  const timed = BOARD_VIEWS.filter(({ seconds }) => rotation[seconds] > 0);
  const filled = timed.filter(
    ({ view }) =>
      (view !== "map" || (map?.revision ?? 0) > 0) &&
      (view !== "roster" ||
        roster.staff.length > 0 ||
        roster.households.length > 0),
  );
  if (filled.length > 0) return filled;
  return timed.length > 0 ? timed : BOARD_VIEWS.slice(0, 1);
};

/** Fills over `seconds`; remounted for each view. */
const Progress = ({ seconds }: { seconds: number }) => {
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setStarted(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <div className="h-1 w-40 overflow-hidden rounded-full bg-rule">
      <div
        className="h-full bg-accent"
        style={{
          width: started ? "100%" : "0%",
          transition: `width ${seconds}s linear`,
        }}
      />
    </div>
  );
};

/**
 * The wall board from the display's own feed: no staff queries, no controls.
 * It rotates lanes, map and roster for the display's `rotation` seconds each
 * (BRI-269), leaving out a view with no time or nothing to show. `14rem` is the
 * display frame's header and padding, which the map fits under.
 */
export const ClinicBoardDisplay = ({
  displayId,
  name,
  visits,
  roster,
  rotation,
}: {
  displayId: string;
  name: string;
  visits: DisplayBoardVisit[] | null;
  roster: ClinicRosterToday | null;
  rotation: BoardRotation | null;
}) => {
  const [step, setStep] = useState(0);
  const [looking, setLooking] = useState<BoardView>("lanes");
  const live = useDisplayLiveMap(displayId, looking === "map");
  const views =
    roster && rotation
      ? rotating(rotation, live, roster)
      : BOARD_VIEWS.slice(0, 1);
  const current = views[step % views.length] ?? BOARD_VIEWS[0]!;
  const view = current.view;
  const seconds = rotation ? rotation[current.seconds] : 0;
  if (looking !== view) setLooking(view);

  useEffect(() => {
    if (views.length < 2) return;
    const timer = setTimeout(() => setStep((s) => s + 1), seconds * 1000);
    return () => clearTimeout(timer);
  }, [step, views.length, seconds]);

  return (
    <DisplayFrame
      tapes={[name]}
      actions={
        <div className="flex flex-col items-end gap-1.5">
          <span className="font-display text-xl text-ink">
            {BOARD_VIEW_LABEL[view]}
          </span>
          {views.length > 1 && <Progress key={step} seconds={seconds} />}
        </div>
      }
    >
      {view === "map" ? (
        // Outside BoardFrame, whose children keep a minimum width: the map
        // has to shrink to fit the screen's height.
        <div className="flex min-h-0 flex-1 justify-center">
          {live && live.revision > 0 ? (
            <LiveClinicMap
              live={live}
              wall
              className="w-full"
              maxHeight="100dvh - 14rem"
            />
          ) : (
            <EmptyNote>No clinic map saved yet.</EmptyNote>
          )}
        </div>
      ) : (
        <BoardFrame className="min-h-0 flex-1 overflow-y-auto">
          {view === "roster" && roster ? (
            <ClinicRosterTables roster={roster} />
          ) : visits ? (
            <BoardLanes
              visits={visits}
              renderCard={(card, now) => (
                <VisitCard
                  card={card}
                  now={now}
                  sprites={card.visits.map((p) => p.visit.sprite)}
                />
              )}
            />
          ) : (
            <BoardSkeleton />
          )}
        </BoardFrame>
      )}
    </DisplayFrame>
  );
};
